'use client';

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/user.store';
import { useVehicleStore } from '@/stores/vehicle.store';
import { useAddressStore } from '@/stores/address.store';
import { usePaymentMethodStore } from '@/stores/paymentMethod.store';
import { useReservationStore } from '@/stores/reservation.store';
import { useMonthlyAccountStore } from '@/stores/monthly-account.store';
import { useCitationStore } from '@/stores/citation.store';
import { useGuestPassStore } from '@/stores/guest-pass.store';
import { useParkingHistoryStore } from '@/stores/parking-history.store';
import { useTicketStore } from '@/stores/ticket.store';
import { useReserveStore } from '@/stores/reserve.store';
import { useDamageClaimStore } from '@/stores/damage-claim.store';
import { useCheckoutActivityStore } from '@/stores/checkout-activity.store';
import { useThemeStore } from '@/stores/theme.store';
import { useUIGaragesStore } from '@/stores/ui-garages.store';

/**
 * Loads all user-scoped data once a session token is present, and clears it
 * when the session ends.
 *
 * ## Invariants:
 * - When the token is lost (logout / session end), EVERY user-scoped store MUST
 *   be reset (vehicle, address, payment method, reservation, monthly account,
 *   citation, guest pass, parking history, ticket, reserve, damage claim).
 *   Why: logout/login is client-side with no hard reload, so these in-memory
 *   stores otherwise survive a user switch and a subsequent login on the same
 *   device could show the previous user's vehicle/address/payment/financial data
 *   (PB-3512 / PB-3104). Any NEW store that caches user- or account-scoped data
 *   must add a reset() and be wired into the logout branch below.
 */
export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const fetchUser = useAuthStore((s) => s.fetchUser);
  const fetchGarageAccounts = useAuthStore((s) => s.fetchGarageAccounts);
  const getAllVehicles = useVehicleStore((s) => s.getAllVehicles);
  const resetVehicles = useVehicleStore((s) => s.reset);
  const getAllAddresses = useAddressStore((s) => s.getAllAddresses);
  const resetAddresses = useAddressStore((s) => s.reset);
  const getAllPaymentMethods = usePaymentMethodStore((s) => s.getAllPaymentMethods);
  const resetPaymentMethods = usePaymentMethodStore((s) => s.reset);
  const getAllReservationsByUser = useReservationStore((s) => s.getAllReservationsByUser);
  const getAllPendingDisclaimertasks = useReservationStore((s) => s.getAllPendingDisclaimertasks);
  const resetReservations = useReservationStore((s) => s.reset);
  const resetMonthlyAccount = useMonthlyAccountStore((s) => s.reset);
  const resetCitations = useCitationStore((s) => s.reset);
  const resetGuestPasses = useGuestPassStore((s) => s.reset);
  const resetParkingHistory = useParkingHistoryStore((s) => s.reset);
  const resetTickets = useTicketStore((s) => s.reset);
  const resetReserve = useReserveStore((s) => s.reset);
  const resetDamageClaims = useDamageClaimStore((s) => s.reset);
  const resetCheckoutActivity = useCheckoutActivityStore((s) => s.reset);
  const fetchUIGarages = useUIGaragesStore((s) => s.fetchUIGarages);
  useEffect(() => {
    if (token) {
      // Fetch all user data from the API
      fetchUser();
      getAllVehicles();
      getAllAddresses();
      getAllPaymentMethods();
      // PB-9495: share one reservations request with the header and the page.
      getAllReservationsByUser({ shareInFlight: true });
      getAllPendingDisclaimertasks();
      fetchGarageAccounts();
      fetchUIGarages();
    } else {
      // Clear all user-scoped data so a subsequent login on the same device
      // cannot see the previous user's data (see invariant above).
      resetVehicles();
      resetAddresses();
      resetPaymentMethods();
      resetReservations();
      resetMonthlyAccount();
      resetCitations();
      resetGuestPasses();
      resetParkingHistory();
      resetTickets();
      resetReserve();
      resetDamageClaims();
      // Drops any checkout hold left behind by a surface that unmounted during sign-out, so the
      // next customer on this device does not inherit a deferred session clock. (PB-5476)
      resetCheckoutActivity();
    }
  }, [token]);
  const isConvergeWebsite = useThemeStore((s) => s.theme?.isConvergeWebsite);
  const getConvergeWebsiteThemes = useThemeStore((s) => s.getConvergeWebsiteThemes);

  useEffect(() => {
    if (isConvergeWebsite) {
      getConvergeWebsiteThemes();
    }
  }, [isConvergeWebsite, getConvergeWebsiteThemes]);

  return <>{children}</>;
}
